import { ArrowRight, Building2, FileText, ListChecks, Scale, Settings, Users } from 'lucide-react';
import { AccessCollectIcon } from '@/components/icons/AccessCollectIcon';
import { Button } from '@/components/ui/button';

interface OnboardingWelcomeProps {
  userName: string;
  onStart: () => void;
  onSkip: () => void;
}

const steps = [
  { icon: Building2, label: 'Company Setup', desc: 'Add and configure your company instances' },
  { icon: Users, label: 'User Access', desc: 'Assign users access to companies' },
  { icon: Settings, label: 'General Settings', desc: 'Set DSO targets, ageing bands & more' },
  { icon: FileText, label: 'Templates', desc: 'Review available document templates' },
  { icon: ListChecks, label: 'Collection Strategies', desc: 'Review collection strategy options' },
  { icon: Scale, label: 'Dispute Types', desc: 'Review dispute type configuration' },
];

export function OnboardingWelcome({ userName, onStart, onSkip }: OnboardingWelcomeProps) {
  const firstName = userName.split(' ')[0];

  return (
    <div className="flex flex-col items-center justify-center h-full px-8 py-12 max-w-3xl mx-auto text-center">
      {/* Logo */}
      <div className="flex items-center justify-center gap-3 mb-8">
        <AccessCollectIcon className="h-14 w-14" />
        <h1 className="text-3xl font-bold">Access Collect</h1>
      </div>

      {/* Greeting */}
      <h2 className="text-2xl font-semibold mb-2">Welcome, {firstName}! 👋</h2>
      <p className="text-muted-foreground text-base mb-10 max-w-lg">
        Let's get your workspace set up. This wizard will guide you through the key configuration steps. It only takes a
        few minutes and you can always come back to it later.
      </p>

      {/* Step overview */}
      <div className="grid grid-cols-2 gap-3 w-full mb-10">
        {steps.map(({ icon: Icon, label, desc }) => (
          <div
            key={label}
            className="flex items-start gap-3 rounded-xl border bg-card p-4 text-left hover:bg-accent/50 transition-colors"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-medium">{label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex flex-col items-center gap-3 w-full">
        <Button size="lg" className="w-full max-w-xs gap-2" onClick={onStart}>
          Let's Start <ArrowRight className="h-4 w-4" />
        </Button>
        <button
          onClick={onSkip}
          className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors"
        >
          Skip onboarding
        </button>
      </div>
    </div>
  );
}
